import React, { ReactElement, useEffect, useState, useRef } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { groupBy, sortBy } from 'ramda'
import { useTheme } from '@mui/material/styles'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchPlanningEvents,
  fetchEventCompliances,
  fetchSyllabus,
  fetchCAPCEAccreditation,
  fetchANCCAccreditation,
  fetchReferences,
  fetchObjectives,
  fetchTemplate,
  fetchInstructors,
  fetchAllEventInstructors,
  fetchQuiz,
  createQuiz,
  fetchQuizQuestions,
  fetchPlanningEventsProgress,
  selectPlanningEvents,
  selectPlanningEventProgress,
  selectPlanningEventProgressByID,
  selectPlanningEventProgressStatus,
  selectEventCompliances,
  selectSyllabusByEventID,
  selectSyllabusStatusByEventID,
  selectCAPCEAccreditationByEventID,
  selectCAPCEAccreditationStatusByEventID,
  selectANCCAccreditationByEventID,
  selectANCCAccreditationStatusByEventID,
  selectReferencesByEventID,
  selectReferencesStatusByEventID,
  selectObjectivesByEventID,
  selectObjectivesStatusByEventID,
  selectTemplateByEventID,
  selectTemplateStatusByEventID,
  selectInstructorsByEventID,
  selectInstructors,
  selectQuizByEventID,
  selectQuizStatusByEventID,
  selectQuizQuestionsByQuizID,
} from 'src/v3/content-pipeline/event-pipeline.slice'
import {
  fetchFOAMfratInstructors,
  selectFOAMfratInstructors,
} from 'src/v3/content-pipeline/instructor.slice'
import { QuizBuilder } from 'src/v3/components/quiz/quiz-builder'
import EventField from './fields/event-field'
import SyllabusFieldEditable from './fields/syllabus-field-editable'
import CAPCEFieldEditable from './fields/capce-field-editable'
import ANCCFieldEditable from './fields/ancc-field-editable'
import EventCreationForm from './event-creation-form'
import SyllabusCreationForm from './syllabus-creation-form'
import CAPCECreationForm from './capce-creation-form'
import ANCCCreationForm from './ancc-creation-form'
import ReferencesManagement from './references-management'
import ObjectivesManagement from './objectives-management'
import TemplateManagement from './template-management'
import InstructorManagement from './instructor-management'
import QuizDataModal from './quiz-data-modal'
import SelectItemHint from './select-item-hint'
import LifecycleChip from '../components/lifecycle/lifecycle-chip'
import {
  LifecycleFilterValue,
  filterByLifecycle,
  lifecycleCounts,
} from '../components/lifecycle/lifecycle-filter'
import PipelineFilterBar from '../components/pipeline-filter-bar'

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let muiTheme = useTheme()
  let elementRef = useRef(null)

  let [activeEvent, setActiveEvent] = useState<string>()
  let [eventFilter, setEventFilter] = useState<
    'all' | 'needs-revision' | 'in-planning' | 'ready-for-review' | 'approved'
  >('needs-revision')
  let [lifecycleFilter, setLifecycleFilter] =
    useState<LifecycleFilterValue>('all')
  let [instructorFilter, setInstructorFilter] = useState<string | null>(null)
  let [searchQuery, setSearchQuery] = useState('')
  let [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  let [showEventForm, setShowEventForm] = useState(false)
  let [editingEvent, setEditingEvent] = useState<any>()
  let [activeTab, setActiveTab] = useState(0)
  let [showSyllabusForm, setShowSyllabusForm] = useState(false)
  let [showCAPCEForm, setShowCAPCEForm] = useState(false)
  let [showANCCForm, setShowANCCForm] = useState(false)
  let [showQuizDataModal, setShowQuizDataModal] = useState(false)

  let planningEvents = useAppSelector(selectPlanningEvents)
  let planningEventProgress = useAppSelector(selectPlanningEventProgress)
  let planningEventProgressStatus = useAppSelector(
    selectPlanningEventProgressStatus,
  )
  let activeEventProgress = useAppSelector(
    selectPlanningEventProgressByID(activeEvent || ''),
  )
  let eventCompliances = useAppSelector(selectEventCompliances)
  let activeEventCompliance = eventCompliances[activeEvent || '']
  let activeEventSyllabus = useAppSelector(
    selectSyllabusByEventID(activeEvent || ''),
  )
  let activeEventSyllabusStatus = useAppSelector(
    selectSyllabusStatusByEventID(activeEvent || ''),
  )
  let activeEventCAPCE = useAppSelector(
    selectCAPCEAccreditationByEventID(activeEvent || ''),
  )
  let activeEventCAPCEStatus = useAppSelector(
    selectCAPCEAccreditationStatusByEventID(activeEvent || ''),
  )
  let activeEventANCC = useAppSelector(
    selectANCCAccreditationByEventID(activeEvent || ''),
  )
  let activeEventANCCStatus = useAppSelector(
    selectANCCAccreditationStatusByEventID(activeEvent || ''),
  )
  let activeEventReferences = useAppSelector(
    selectReferencesByEventID(activeEvent || ''),
  )
  let activeEventReferencesStatus = useAppSelector(
    selectReferencesStatusByEventID(activeEvent || ''),
  )
  let activeEventObjectives = useAppSelector(
    selectObjectivesByEventID(activeEvent || ''),
  )
  let activeEventObjectivesStatus = useAppSelector(
    selectObjectivesStatusByEventID(activeEvent || ''),
  )
  let activeEventTemplate = useAppSelector(
    selectTemplateByEventID(activeEvent || ''),
  )
  let activeEventTemplateStatus = useAppSelector(
    selectTemplateStatusByEventID(activeEvent || ''),
  )
  let activeEventInstructors = useAppSelector(
    selectInstructorsByEventID(activeEvent || ''),
  )
  let foamfratInstructors = useAppSelector(selectFOAMfratInstructors)
  let planningInstructorMap = useAppSelector(selectInstructors)
  let activeEventQuiz = useAppSelector(selectQuizByEventID(activeEvent || ''))
  let activeEventQuizStatus = useAppSelector(
    selectQuizStatusByEventID(activeEvent || ''),
  )
  let activeEventQuizQuestions = useAppSelector(
    selectQuizQuestionsByQuizID(activeEvent || ''),
  )

  let activeEventData = planningEvents.find((event) => event.id === activeEvent)

  // Calculate syllabus completion percentage
  let syllabusCompletionPercentage = activeEventSyllabus
    ? (() => {
        let requiredFields = [
          'text',
          'practiceGap',
          'practiceGapEvidence',
          'learnerEngagement',
          'learningOutcomes',
        ]
        let completedFields = requiredFields.filter(
          (field) =>
            activeEventSyllabus[field as keyof typeof activeEventSyllabus] &&
            String(
              activeEventSyllabus[field as keyof typeof activeEventSyllabus],
            ).trim().length > 0,
        ).length
        return Math.round((completedFields / requiredFields.length) * 100)
      })()
    : 0

  // Calculate accreditation completion percentage
  let accreditationCompletionPercentage = activeEvent
    ? (() => {
        let accreditationItems = [
          !!activeEventCAPCE, // 1. CAPCE accreditation (as a whole)
          !!activeEventANCC, // 2. ANCC accreditation (as a whole)
          activeEventInstructors && activeEventInstructors.length > 0, // 3. At least one instructor
          !!activeEventTemplate, // 4. Template assigned
        ]

        let completedItems = accreditationItems.filter(Boolean).length
        let totalItems = accreditationItems.length

        return Math.round((completedItems / totalItems) * 100)
      })()
    : 0

  // Calculate quiz completion percentage based on questions
  let quizCompletionPercentage = activeEventQuiz ? 100 : 0

  // Helper function to calculate workflow status for an event
  let getEventWorkflowStatus = (
    event: (typeof planningEvents)[number],
  ): 'needs-revision' | 'in-planning' | 'ready-for-review' | 'approved' => {
    let progress = planningEventProgress[event.id]
    if (!progress) return 'in-planning'

    let dataComplete =
      (progress?.dataCompleteness?.percentComplete ?? 0) === 100
    let complianceComplete =
      (progress?.complianceStatus?.percentApproved ?? 0) === 100
    let hasRejected = (progress?.complianceStatus?.rejected ?? 0) > 0

    if (hasRejected) return 'needs-revision'
    if (!dataComplete) return 'in-planning'
    if (!complianceComplete) return 'ready-for-review'
    return 'approved'
  }

  let lifecycleFilteredEvents = filterByLifecycle(
    planningEvents,
    lifecycleFilter,
  )

  // Group events by workflow status
  let {
    needsRevisionEvents,
    inPlanningEvents,
    readyForReviewEvents,
    approvedEvents,
  } = groupBy((event: (typeof planningEvents)[number]) => {
    let status = getEventWorkflowStatus(event)
    return status === 'needs-revision'
      ? 'needsRevisionEvents'
      : status === 'in-planning'
        ? 'inPlanningEvents'
        : status === 'ready-for-review'
          ? 'readyForReviewEvents'
          : 'approvedEvents'
  })(lifecycleFilteredEvents)

  let baseFilteredEvents =
    eventFilter === 'needs-revision'
      ? needsRevisionEvents
      : eventFilter === 'in-planning'
        ? inPlanningEvents
        : eventFilter === 'ready-for-review'
          ? readyForReviewEvents
          : eventFilter === 'approved'
            ? approvedEvents
            : lifecycleFilteredEvents

  // Apply search filter
  let searchFilteredEvents = baseFilteredEvents?.filter((event) =>
    event.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  let instructorFilteredEvents = instructorFilter
    ? searchFilteredEvents?.filter((event) =>
        (planningInstructorMap[event.id] ?? []).some(
          (i) => i.id === instructorFilter,
        ),
      )
    : searchFilteredEvents

  let filteredEvents = instructorFilteredEvents
    ? sortBy((e) => {
        let t = e.createdTime ? new Date(e.createdTime).getTime() : 0
        return sortDir === 'asc' ? -t : t
      }, instructorFilteredEvents)
    : []

  // Calculate counts for new workflow statuses
  let totalNeedsRevision = needsRevisionEvents?.length ?? 0
  let totalInPlanning = inPlanningEvents?.length ?? 0
  let totalReadyForReview = readyForReviewEvents?.length ?? 0
  let totalApproved = approvedEvents?.length ?? 0
  let totalFieldsPending = planningEvents.reduce((sum, event) => {
    let progress = planningEventProgress[event.id]
    return sum + (progress?.dataCompleteness?.missingFields?.length ?? 0)
  }, 0)

  // Set default filter based on what needs attention
  useEffect(() => {
    if (
      planningEventProgressStatus === 'fulfilled' &&
      eventFilter === 'needs-revision'
    ) {
      // If no events need revision, switch to "in-planning"
      if (totalNeedsRevision === 0 && totalInPlanning > 0) {
        setEventFilter('in-planning')
      }
    }
  }, [planningEventProgressStatus, totalNeedsRevision, totalInPlanning])

  // Refetch every time this dashboard is landed on (mounted), rather than
  // only on first-ever load, so switching tabs and coming back shows fresh data.
  useEffect(() => {
    dispatch(fetchPlanningEvents())
    dispatch(fetchAllEventInstructors())
    dispatch(fetchFOAMfratInstructors())
    dispatch(fetchPlanningEventsProgress())
    dispatch(fetchEventCompliances())
  }, [dispatch])

  useEffect(() => {
    if (elementRef.current && window.innerWidth < 900) {
      let prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches
      ;(elementRef.current as any).scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      })
    }
  }, [activeEvent])

  useEffect(() => {
    if (!filteredEvents?.some((event) => event.id === activeEvent)) {
      setActiveEvent(undefined)
    }
  }, [eventFilter, searchQuery, lifecycleFilter, instructorFilter])

  useEffect(() => {
    if (activeEvent) {
      // Always fetch syllabus when active event changes, regardless of status
      dispatch(fetchSyllabus(activeEvent))
      // Also fetch CAPCE and ANCC accreditations
      dispatch(fetchCAPCEAccreditation(activeEvent))
      dispatch(fetchANCCAccreditation(activeEvent))
      // Always try to fetch references and objectives - let the API return 404 if no syllabus exists
      dispatch(fetchReferences(activeEvent))
      dispatch(fetchObjectives(activeEvent))
      // Fetch template assignment
      dispatch(fetchTemplate(activeEvent))
      // Fetch instructor assignments
      dispatch(fetchInstructors(activeEvent))
      // Fetch quiz
      dispatch(fetchQuiz(activeEvent))
    }
  }, [activeEvent])

  useEffect(() => {
    if (activeEvent && activeEventQuiz) {
      dispatch(fetchQuizQuestions(activeEvent))
    }
  }, [activeEvent, activeEventQuiz])

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
        Live Classes (F5) - Content Planning Dashboard
      </Typography>
      <Typography
        color="text.secondary"
        variant="body2"
        sx={{
          mb: 3,
        }}
      >
        Plan and develop live class events with comprehensive syllabus
        development, accreditation setup, and compliance tracking. Live classes
        are scheduled events where instructors teach in real-time. Create
        events, manage templates, assign instructors, and track progress through
        the content pipeline.
      </Typography>
      <Card
        variant="outlined"
        sx={{ mb: 2, p: 3 }}
        component="aside"
        aria-label="Overview statistics"
      >
        <Typography variant="h6" component="h3">
          Overview
        </Typography>
        <Divider sx={{ my: 1 }} />
        {planningEventProgressStatus === 'fulfilled' ? (
          <Box
            sx={{ display: 'flex', columnGap: 4, rowGap: 1, flexWrap: 'wrap' }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: muiTheme.palette.error.main,
                }}
              >
                {totalNeedsRevision}
              </Typography>
              <Typography variant="subtitle1">Needs Revision</Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: muiTheme.palette.contentPipelineColors.planning,
                }}
              >
                {totalInPlanning}
              </Typography>
              <Typography variant="subtitle1">In Planning</Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: muiTheme.palette.contentPipelineColors.compliance,
                }}
              >
                {totalReadyForReview}
              </Typography>
              <Typography variant="subtitle1">Ready for Review</Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: muiTheme.palette.success.main,
                }}
              >
                {totalApproved}
              </Typography>
              <Typography variant="subtitle1">Approved</Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: muiTheme.palette.contentPipelineColors.planning,
                }}
              >
                {totalFieldsPending}
              </Typography>
              <Typography variant="subtitle1">Fields Remaining</Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{ display: 'flex', columnGap: 4, rowGap: 1, flexWrap: 'wrap' }}
          >
            {[...Array(5)].map((_, i) => (
              <Box key={i} sx={{ minWidth: 120 }}>
                <Skeleton
                  variant="text"
                  sx={{
                    width: 60,
                    height: 32,
                    mb: 0.5,
                  }}
                />
                <Skeleton
                  variant="text"
                  sx={{
                    width: 120,
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Card>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1.5,
          }}
        >
          <Typography variant="h5" component="h2">
            Planning Events
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setEditingEvent(undefined)
              setShowEventForm(true)
            }}
            aria-label="Create new planning event"
          >
            + New Event
          </Button>
        </Box>
        <PipelineFilterBar
          search={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search events…"
          status={eventFilter}
          onStatusChange={setEventFilter}
          statusCounts={{
            needsRevision: totalNeedsRevision,
            inPlanning: totalInPlanning,
            readyForReview: totalReadyForReview,
            approved: totalApproved,
            all: lifecycleFilteredEvents?.length ?? 0,
          }}
          lifecycle={lifecycleFilter}
          onLifecycleChange={setLifecycleFilter}
          lifecycleCounts={lifecycleCounts(planningEvents)}
          instructorFilter={instructorFilter}
          onInstructorChange={setInstructorFilter}
          instructors={foamfratInstructors}
          sort={sortDir}
          onSortChange={setSortDir}
        />
      </Paper>
      <Box className="grid grid-cols-1 md:grid-cols-[350px_1fr] gap-6 items-start">
        <Box
          sx={{
            position: { md: 'sticky' },
            top: { md: 16 },
            maxHeight: { md: 'calc(100vh - 32px)' },
            overflowY: { md: 'auto' },
          }}
        >
          <Paper
            sx={{ p: 3 }}
            component="nav"
            aria-label="Planning events list"
          >
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
              role="list"
              aria-label="Planning events"
            >
              {filteredEvents?.length === 0 ? (
                <Box
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    bgcolor: 'background.paperDark',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    No Events Found
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {eventFilter === 'needs-revision'
                      ? 'Great! No events currently need revision.'
                      : eventFilter === 'in-planning'
                        ? 'No events are currently in planning.'
                        : eventFilter === 'ready-for-review'
                          ? 'No events are ready for compliance review.'
                          : eventFilter === 'approved'
                            ? 'No events have been fully approved yet.'
                            : searchQuery
                              ? `No events match "${searchQuery}".`
                              : 'No events available.'}
                  </Typography>
                </Box>
              ) : (
                filteredEvents?.map((event) => {
                  let checked = activeEvent === event.id
                  let progress = planningEventProgress[event.id]
                  let dataProgress =
                    progress?.dataCompleteness?.percentComplete || 0
                  let complianceProgress =
                    progress?.complianceStatus?.percentApproved || 0
                  let workflowStatus = getEventWorkflowStatus(event)

                  // Determine status badge color and label
                  let statusConfig = {
                    'needs-revision': {
                      label: 'Needs Revision',
                      color: 'error' as const,
                    },
                    'in-planning': {
                      label: 'In Planning',
                      color: 'primary' as const,
                    },
                    'ready-for-review': {
                      label: 'Ready for Review',
                      color: 'info' as const,
                    },
                    approved: {
                      label: 'Approved',
                      color: 'success' as const,
                    },
                  }[workflowStatus]

                  return (
                    <Box role="listitem" key={event.id}>
                      <Card
                        variant="outlined"
                        component="button"
                        sx={{
                          p: 1.5,
                          bgcolor: checked
                            ? 'rgba(25, 118, 210, 0.12)'
                            : undefined,
                          '&:hover': {
                            cursor: 'pointer',
                            bgcolor: checked
                              ? 'rgba(25, 118, 210, 0.12)'
                              : 'divider',
                          },
                          '&:focus': {
                            outline: '2px solid',
                            outlineColor: 'primary.main',
                            outlineOffset: '2px',
                          },
                          border: 'none',
                          textAlign: 'left',
                          width: '100%',
                        }}
                        onClick={() => setActiveEvent(event.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setActiveEvent(event.id)
                          }
                        }}
                        aria-pressed={checked}
                        aria-label={`Select ${event.name} - ${statusConfig.label} - ${dataProgress}% data entry complete, ${complianceProgress}% compliance complete`}
                        tabIndex={0}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                            }}
                          >
                            {event.name}
                          </Typography>
                          <LifecycleChip
                            lifecycleState={event.lifecycleState}
                          />
                        </Box>
                        <Box sx={{ mt: 1, mb: 1.5 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 0.5,
                            }}
                          >
                            <Typography variant="caption">
                              Data Entry
                            </Typography>
                            <Typography variant="caption">
                              {dataProgress}%
                            </Typography>
                          </Box>
                          <Box
                            role="status"
                            aria-live="polite"
                            aria-atomic="true"
                          >
                            <LinearProgress
                              variant="determinate"
                              value={dataProgress}
                              aria-label={`Data entry progress: ${dataProgress}%`}
                              sx={{
                                height: 4,
                                borderRadius: 2,
                                mb: 1,
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor:
                                    muiTheme.palette.contentPipelineColors
                                      .planning,
                                },
                              }}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 0.5,
                            }}
                          >
                            <Typography variant="caption">
                              Compliance
                            </Typography>
                            <Typography variant="caption">
                              {complianceProgress}%
                            </Typography>
                          </Box>
                          <Box
                            role="status"
                            aria-live="polite"
                            aria-atomic="true"
                          >
                            <LinearProgress
                              variant="determinate"
                              value={complianceProgress}
                              aria-label={`Compliance progress: ${complianceProgress}%`}
                              sx={{
                                height: 4,
                                borderRadius: 2,
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor:
                                    muiTheme.palette.contentPipelineColors
                                      .compliance,
                                },
                              }}
                            />
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography variant="subtitle2">
                            {event.hours || 0}h •{' '}
                            {typeof event.category === 'object' &&
                            event.category !== null
                              ? (event.category as any).kind === 'other'
                                ? (event.category as any).other
                                : (event.category as any).kind
                              : event.category}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              alignItems: 'center',
                              flexWrap: 'wrap',
                            }}
                          >
                            {(progress?.complianceStatus?.rejected ?? 0) >
                              0 && (
                              <Chip
                                color="error"
                                size="small"
                                label={`${progress.complianceStatus.rejected} rejected`}
                                variant="outlined"
                                aria-label={`${progress.complianceStatus.rejected} ${progress.complianceStatus.rejected === 1 ? 'field' : 'fields'} rejected and need revision`}
                              />
                            )}
                            {(progress?.dataCompleteness?.missingFields
                              ?.length ?? 0) > 0 && (
                              <Chip
                                size="small"
                                label={`${progress.dataCompleteness.missingFields.length} missing`}
                                sx={{
                                  borderColor:
                                    muiTheme.palette.contentPipelineColors
                                      .planning,
                                  color:
                                    muiTheme.palette.contentPipelineColors
                                      .planning,
                                }}
                                variant="outlined"
                                aria-label={`${progress.dataCompleteness.missingFields.length} ${progress.dataCompleteness.missingFields.length === 1 ? 'field' : 'fields'} still need to be completed`}
                              />
                            )}
                            <Chip
                              size="small"
                              label={statusConfig.label}
                              variant={checked ? 'filled' : 'outlined'}
                              aria-label={`Event status: ${statusConfig.label}`}
                              color={statusConfig.color}
                            />
                          </Box>
                        </Box>
                      </Card>
                    </Box>
                  )
                })
              )}
            </Box>
          </Paper>
        </Box>

        <Box ref={elementRef} role="main" aria-label="Planning details">
          {/* Header and Progress Section */}
          {activeEvent && activeEventData && activeEventProgress && (
            <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{ fontWeight: 700 }}
                >
                  Planning: {activeEventData.name}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  component="a"
                  href={`/events/${activeEvent}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ ml: 2 }}
                >
                  View Event
                </Button>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'medium',
                    }}
                  >
                    Data Entry Progress
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {activeEventProgress?.dataCompleteness?.percentComplete ??
                      0}
                    %
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={
                    activeEventProgress?.dataCompleteness?.percentComplete ?? 0
                  }
                  aria-label={`Data entry progress: ${activeEventProgress?.dataCompleteness?.percentComplete ?? 0}%`}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor:
                        muiTheme.palette.contentPipelineColors.planning,
                    },
                  }}
                />
              </Box>

              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'medium',
                    }}
                  >
                    Compliance Progress
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {activeEventProgress?.complianceStatus?.percentApproved ??
                      0}
                    %
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={
                    activeEventProgress?.complianceStatus?.percentApproved ?? 0
                  }
                  aria-label={`Compliance progress: ${activeEventProgress?.complianceStatus?.percentApproved ?? 0}%`}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor:
                        muiTheme.palette.contentPipelineColors.compliance,
                    },
                  }}
                />
              </Box>

              {/* Rejected Fields Alert */}
              {(activeEventProgress?.complianceStatus?.rejected ?? 0) > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'medium',
                      mb: 0.5,
                    }}
                  >
                    {activeEventProgress.complianceStatus.rejected}{' '}
                    {activeEventProgress.complianceStatus.rejected === 1
                      ? 'Field'
                      : 'Fields'}{' '}
                    Rejected
                  </Typography>
                  <Typography variant="body2">
                    The following fields need revision:{' '}
                    {activeEventProgress.complianceStatus.rejectedFields.map(
                      (field, idx) => (
                        <span key={field}>
                          <strong>{field}</strong>
                          {idx <
                          activeEventProgress.complianceStatus.rejectedFields
                            .length -
                            1
                            ? ', '
                            : ''}
                        </span>
                      ),
                    )}
                  </Typography>
                </Alert>
              )}

              {/* Missing Fields Info */}
              {(activeEventProgress?.dataCompleteness?.missingFields?.length ??
                0) > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'background.paperDark',
                    borderRadius: 1,
                    borderLeft: 4,
                    borderColor:
                      muiTheme.palette.contentPipelineColors.planning,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'medium',
                      mb: 0.5,
                    }}
                  >
                    {activeEventProgress.dataCompleteness.missingFields.length}{' '}
                    {activeEventProgress.dataCompleteness.missingFields
                      .length === 1
                      ? 'Field'
                      : 'Fields'}{' '}
                    Remaining
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {activeEventProgress.dataCompleteness.missingFields.join(
                      ', ',
                    )}
                  </Typography>
                </Box>
              )}

              {/* Pending Review Info */}
              {(activeEventProgress?.complianceStatus?.pending ?? 0) > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'background.paperDark',
                    borderRadius: 1,
                    borderLeft: 4,
                    borderColor:
                      muiTheme.palette.contentPipelineColors.compliance,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'medium',
                      mb: 0.5,
                    }}
                  >
                    {activeEventProgress.complianceStatus.pending}{' '}
                    {activeEventProgress.complianceStatus.pending === 1
                      ? 'Field'
                      : 'Fields'}{' '}
                    Pending Review
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {activeEventProgress.complianceStatus.pendingFields.join(
                      ', ',
                    )}
                  </Typography>
                </Box>
              )}

              {/* Task Overview Cards */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 2,
                  mt: 3,
                }}
                role="group"
                aria-label="Section completion overview"
              >
                <Card
                  elevation={0}
                  component="button"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor:
                      activeTab === 0
                        ? 'action.selected'
                        : 'background.paperDark',
                    '&:hover': { bgcolor: 'action.hover' },
                    border: 'none',
                  }}
                  onClick={() => setActiveTab(0)}
                  aria-label={`Event Details - ${activeEventProgress?.dataCompleteness?.percentComplete ?? 0}% complete`}
                  aria-pressed={activeTab === 0}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 0.5,
                      color: muiTheme.palette.contentPipelineColors.planning,
                    }}
                  >
                    {activeEventProgress?.dataCompleteness?.percentComplete ??
                      0}
                    %
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Event Details
                  </Typography>
                </Card>

                <Card
                  elevation={0}
                  component="button"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor:
                      activeTab === 1
                        ? 'action.selected'
                        : 'background.paperDark',
                    '&:hover': { bgcolor: 'action.hover' },
                    border: 'none',
                  }}
                  onClick={() => setActiveTab(1)}
                  aria-label={`Accreditation & Setup - ${accreditationCompletionPercentage > 0 ? `${accreditationCompletionPercentage}% complete` : 'Not started'}`}
                  aria-pressed={activeTab === 1}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 0.5,
                      color: muiTheme.palette.contentPipelineColors.planning,
                    }}
                  >
                    {accreditationCompletionPercentage > 0
                      ? `${accreditationCompletionPercentage}%`
                      : '—'}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Accreditation
                  </Typography>
                </Card>

                <Card
                  elevation={0}
                  component="button"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor:
                      activeTab === 2
                        ? 'action.selected'
                        : 'background.paperDark',
                    '&:hover': { bgcolor: 'action.hover' },
                    border: 'none',
                  }}
                  onClick={() => setActiveTab(2)}
                  aria-label={`Syllabus - ${syllabusCompletionPercentage > 0 ? `${syllabusCompletionPercentage}% complete` : 'Not started'}`}
                  aria-pressed={activeTab === 2}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 0.5,
                      color: muiTheme.palette.contentPipelineColors.planning,
                    }}
                  >
                    {syllabusCompletionPercentage > 0
                      ? `${syllabusCompletionPercentage}%`
                      : '—'}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Syllabus
                  </Typography>
                </Card>

                <Card
                  elevation={0}
                  component="button"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor:
                      activeTab === 3
                        ? 'action.selected'
                        : 'background.paperDark',
                    '&:hover': { bgcolor: 'action.hover' },
                    border: 'none',
                  }}
                  onClick={() => setActiveTab(3)}
                  aria-label={`Assessment - ${quizCompletionPercentage > 0 ? `${quizCompletionPercentage}% complete` : 'Not started'}`}
                  aria-pressed={activeTab === 3}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 0.5,
                      color: muiTheme.palette.contentPipelineColors.planning,
                    }}
                  >
                    {quizCompletionPercentage > 0
                      ? `${quizCompletionPercentage}%`
                      : '—'}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Assessment
                  </Typography>
                </Card>
              </Box>
            </Card>
          )}

          {/* Empty state */}
          {!activeEvent && <SelectItemHint type="event" />}

          {/* Tabs */}
          {activeEvent && activeEventData && activeEventProgress && (
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons
              allowScrollButtonsMobile
              sx={{ mb: 3 }}
              aria-label="Planning section tabs"
            >
              <Tab
                label="Event Details"
                id="tab-0"
                aria-controls="tabpanel-0"
              />
              <Tab
                label="Accreditation & Setup"
                id="tab-1"
                aria-controls="tabpanel-1"
              />
              <Tab label="Syllabus" id="tab-2" aria-controls="tabpanel-2" />
              <Tab label="Assessment" id="tab-3" aria-controls="tabpanel-3" />
            </Tabs>
          )}

          {/* Tab Content - Direct on black background (Form Mode) */}
          {activeEvent && activeEventData && activeEventProgress && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Tab 0: Event Details */}
              {activeTab === 0 && (
                <Box role="tabpanel" id="tabpanel-0" aria-labelledby="tab-0">
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                  >
                    <EventField
                      eventID={activeEvent}
                      event={activeEventData}
                      fieldName="name"
                      fieldLabel="Name"
                      fieldType="text"
                      complianceStatus={activeEventCompliance?.name}
                      required
                      viewMode="inline"
                    />

                    <EventField
                      eventID={activeEvent}
                      event={activeEventData}
                      fieldName="description"
                      fieldLabel="Description (Not used in title screen)"
                      fieldType="textarea"
                      complianceStatus={activeEventCompliance?.description}
                      required
                      viewMode="inline"
                    />

                    <Box className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <EventField
                        eventID={activeEvent}
                        event={activeEventData}
                        fieldName="hours"
                        fieldLabel="Length (Hours)"
                        fieldType="number"
                        complianceStatus={activeEventCompliance?.hours}
                        required
                        viewMode="inline"
                      />

                      <EventField
                        eventID={activeEvent}
                        event={activeEventData}
                        fieldName="category"
                        fieldLabel="Category"
                        fieldType="select"
                        complianceStatus={activeEventCompliance?.category}
                        required
                        viewMode="inline"
                      />
                    </Box>

                    <EventField
                      eventID={activeEvent}
                      event={activeEventData}
                      fieldName="quizPasscode"
                      fieldLabel="Event Passcode"
                      fieldType="text"
                      complianceStatus="new" // TODO: Replace with activeEventCompliance?.quizPasscode when compliance dashboard is implemented
                      required
                      viewMode="inline"
                    />

                    <EventField
                      eventID={activeEvent}
                      event={activeEventData}
                      fieldName="virtualLocationUrl"
                      fieldLabel="Zoom Meeting URL"
                      fieldType="text"
                      complianceStatus="new" // TODO: Replace with activeEventCompliance?.virtualLocationUrl when compliance dashboard is implemented
                      required
                      viewMode="inline"
                    />

                    <EventField
                      eventID={activeEvent}
                      event={activeEventData}
                      fieldName="thumbnail"
                      fieldLabel="Thumbnail Image"
                      fieldType="image"
                      complianceStatus={activeEventCompliance?.thumbnail}
                      viewMode="inline"
                    />

                    <EventField
                      eventID={activeEvent}
                      event={activeEventData}
                      fieldName="banner"
                      fieldLabel="Banner Image"
                      fieldType="image"
                      complianceStatus={activeEventCompliance?.banner}
                      viewMode="inline"
                    />

                    {activeEventProgress?.dataCompleteness?.percentComplete ===
                      100 && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          color="success"
                          variant="contained"
                          aria-label="Submit completed course for compliance review"
                        >
                          Submit for Review
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Tab 1: Accreditation & Setup */}
              {activeTab === 1 && (
                <Box role="tabpanel" id="tabpanel-1" aria-labelledby="tab-1">
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    sx={{
                      mb: 3,
                    }}
                  >
                    Configure CAPCE accreditation, ANCC accreditation,
                    instructors, and event template.
                  </Typography>

                  {!activeEventCAPCE &&
                    activeEventCAPCEStatus !== 'pending' && (
                      <Box
                        sx={{
                          p: 3,
                          bgcolor: 'background.paperDark',
                          borderRadius: 1,
                          textAlign: 'center',
                          mb: 3,
                        }}
                      >
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          No CAPCE Accreditation Created
                        </Typography>
                        <Typography
                          color="text.secondary"
                          variant="body2"
                          sx={{
                            mb: 2,
                          }}
                        >
                          Create CAPCE accreditation for EMS continuing
                          education credit.
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={() => setShowCAPCEForm(true)}
                          aria-label="Create CAPCE accreditation"
                        >
                          Create CAPCE Accreditation
                        </Button>
                      </Box>
                    )}

                  {activeEventCAPCEStatus === 'pending' && (
                    <Box
                      sx={{
                        p: 3,
                        bgcolor: 'background.paperDark',
                        borderRadius: 1,
                        textAlign: 'center',
                        mb: 3,
                      }}
                    >
                      <Typography color="text.secondary" variant="body1">
                        Loading CAPCE accreditation...
                      </Typography>
                    </Box>
                  )}

                  {/* CAPCE Accreditation Fields */}
                  {activeEventCAPCE && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        mb: 6,
                        p: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Typography
                        color="primary.main"
                        variant="h6"
                        component="h4"
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                        }}
                      >
                        CAPCE Accreditation Details
                      </Typography>

                      {/* Overall CAPCE Compliance Status */}
                      {activeEventCompliance?.capce && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                            p: 2,
                            bgcolor: 'background.paperDark',
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 'medium',
                            }}
                          >
                            CAPCE Accreditation Status
                          </Typography>
                          <Chip
                            label={
                              activeEventCompliance.capce === 'approved'
                                ? 'Approved'
                                : activeEventCompliance.capce === 'rejected'
                                  ? 'Rejected'
                                  : activeEventCompliance.capce === 'updated'
                                    ? 'Updated - Pending Review'
                                    : activeEventCompliance.capce ===
                                        'unapproved'
                                      ? 'Unapproved'
                                      : 'Pending Review'
                            }
                            color={
                              activeEventCompliance.capce === 'approved'
                                ? 'success'
                                : activeEventCompliance.capce === 'rejected' ||
                                    activeEventCompliance.capce === 'unapproved'
                                  ? 'error'
                                  : 'primary'
                            }
                          />
                        </Box>
                      )}

                      <CAPCEFieldEditable
                        eventID={activeEvent}
                        accreditation={activeEventCAPCE}
                        fieldName="courseCode"
                        fieldLabel="Course Code"
                        fieldType="text"
                        required
                        viewMode="inline"
                      />

                      <Box className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <CAPCEFieldEditable
                          eventID={activeEvent}
                          accreditation={activeEventCAPCE}
                          fieldName="topicArea"
                          fieldLabel="Topic Area"
                          fieldType="select"
                          required
                          viewMode="inline"
                        />

                        <CAPCEFieldEditable
                          eventID={activeEvent}
                          accreditation={activeEventCAPCE}
                          fieldName="classType"
                          fieldLabel="Class Type"
                          fieldType="select"
                          required
                          viewMode="inline"
                        />
                      </Box>

                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 'medium',
                          mt: 1,
                        }}
                      >
                        Important Dates
                      </Typography>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            md: 'repeat(3, 1fr)',
                          },
                          gap: 3,
                        }}
                      >
                        <CAPCEFieldEditable
                          eventID={activeEvent}
                          accreditation={activeEventCAPCE}
                          fieldName="applicationDate"
                          fieldLabel="Application Date"
                          fieldType="date"
                          required
                          viewMode="inline"
                        />

                        <CAPCEFieldEditable
                          eventID={activeEvent}
                          accreditation={activeEventCAPCE}
                          fieldName="submissionDate"
                          fieldLabel="Submission Date"
                          fieldType="date"
                          viewMode="inline"
                        />

                        <CAPCEFieldEditable
                          eventID={activeEvent}
                          accreditation={activeEventCAPCE}
                          fieldName="expirationDate"
                          fieldLabel="Expiration Date"
                          fieldType="date"
                          required
                          viewMode="inline"
                        />
                      </Box>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            md: 'repeat(2, 1fr)',
                          },
                          gap: 3,
                        }}
                      >
                        <CAPCEFieldEditable
                          eventID={activeEvent}
                          accreditation={activeEventCAPCE}
                          fieldName="allClearDate"
                          fieldLabel="All Clear Date"
                          fieldType="date"
                          viewMode="inline"
                        />

                        <CAPCEFieldEditable
                          eventID={activeEvent}
                          accreditation={activeEventCAPCE}
                          fieldName="goLiveDate"
                          fieldLabel="Go Live Date"
                          fieldType="date"
                          viewMode="inline"
                        />
                      </Box>
                    </Box>
                  )}

                  {!activeEventANCC && activeEventANCCStatus !== 'pending' && (
                    <Box
                      sx={{
                        p: 3,
                        bgcolor: 'background.paperDark',
                        borderRadius: 1,
                        textAlign: 'center',
                        mb: 3,
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        No ANCC Accreditation Created
                      </Typography>
                      <Typography
                        color="text.secondary"
                        variant="body2"
                        sx={{
                          mb: 2,
                        }}
                      >
                        Create ANCC accreditation for nursing continuing
                        education credit.
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => setShowANCCForm(true)}
                        aria-label="Create ANCC accreditation"
                      >
                        Create ANCC Accreditation
                      </Button>
                    </Box>
                  )}

                  {activeEventANCCStatus === 'pending' && (
                    <Box
                      sx={{
                        p: 3,
                        bgcolor: 'background.paperDark',
                        borderRadius: 1,
                        textAlign: 'center',
                        mb: 3,
                      }}
                    >
                      <Typography color="text.secondary" variant="body1">
                        Loading ANCC accreditation...
                      </Typography>
                    </Box>
                  )}

                  {/* ANCC Accreditation Fields */}
                  {activeEventANCC && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        mb: 4,
                        p: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Typography
                        color="primary.main"
                        variant="h6"
                        component="h4"
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                        }}
                      >
                        ANCC Accreditation Details
                      </Typography>

                      {/* Overall ANCC Compliance Status */}
                      {activeEventCompliance?.ancc && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                            p: 2,
                            bgcolor: 'background.paperDark',
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 'medium',
                            }}
                          >
                            ANCC Accreditation Status
                          </Typography>
                          <Chip
                            label={
                              activeEventCompliance.ancc === 'approved'
                                ? 'Approved'
                                : activeEventCompliance.ancc === 'rejected'
                                  ? 'Rejected'
                                  : activeEventCompliance.ancc === 'updated'
                                    ? 'Updated - Pending Review'
                                    : activeEventCompliance.ancc ===
                                        'unapproved'
                                      ? 'Unapproved'
                                      : 'Pending Review'
                            }
                            color={
                              activeEventCompliance.ancc === 'approved'
                                ? 'success'
                                : activeEventCompliance.ancc === 'rejected' ||
                                    activeEventCompliance.ancc === 'unapproved'
                                  ? 'error'
                                  : 'primary'
                            }
                          />
                        </Box>
                      )}

                      <ANCCFieldEditable
                        eventID={activeEvent}
                        accreditation={activeEventANCC}
                        fieldName="courseCode"
                        fieldLabel="Course Code"
                        fieldType="text"
                        required
                        viewMode="inline"
                      />

                      <ANCCFieldEditable
                        eventID={activeEvent}
                        accreditation={activeEventANCC}
                        fieldName="expirationDate"
                        fieldLabel="Expiration Date"
                        fieldType="date"
                        required
                        viewMode="inline"
                      />

                      <Box className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ANCCFieldEditable
                          eventID={activeEvent}
                          accreditation={activeEventANCC}
                          fieldName="targetAudience"
                          fieldLabel="Target Audience"
                          fieldType="multiselect"
                          required
                          viewMode="inline"
                        />

                        <ANCCFieldEditable
                          eventID={activeEvent}
                          accreditation={activeEventANCC}
                          fieldName="educationalNeeds"
                          fieldLabel="Educational Needs"
                          fieldType="multiselect"
                          required
                          viewMode="inline"
                        />
                      </Box>

                      <ANCCFieldEditable
                        eventID={activeEvent}
                        accreditation={activeEventANCC}
                        fieldName="competency"
                        fieldLabel="Competency"
                        fieldType="textarea"
                        viewMode="inline"
                      />

                      <ANCCFieldEditable
                        eventID={activeEvent}
                        accreditation={activeEventANCC}
                        fieldName="professionalSource"
                        fieldLabel="Professional Source"
                        fieldType="textarea"
                        viewMode="inline"
                      />
                    </Box>
                  )}

                  <Box sx={{ mb: 4 }}>
                    <TemplateManagement
                      eventID={activeEvent}
                      template={activeEventTemplate}
                      templateStatus={activeEventTemplateStatus}
                      complianceStatus={activeEventCompliance?.template}
                    />
                  </Box>

                  <InstructorManagement
                    eventID={activeEvent}
                    instructors={activeEventInstructors}
                    complianceStatus={activeEventCompliance?.instructors}
                  />
                </Box>
              )}

              {/* Tab 2: Syllabus */}
              {activeTab === 2 && (
                <Box role="tabpanel" id="tabpanel-2" aria-labelledby="tab-2">
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    sx={{
                      mb: 3,
                    }}
                  >
                    Add course objectives and references to build a
                    comprehensive syllabus.
                  </Typography>

                  {!activeEventSyllabus &&
                    activeEventSyllabusStatus !== 'pending' && (
                      <Box
                        sx={{
                          p: 3,
                          bgcolor: 'background.paperDark',
                          borderRadius: 1,
                          textAlign: 'center',
                          mb: 3,
                        }}
                      >
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          No Syllabus Created
                        </Typography>
                        <Typography
                          color="text.secondary"
                          variant="body2"
                          sx={{
                            mb: 2,
                          }}
                        >
                          Create a comprehensive syllabus for your course with
                          AI assistance or manual entry.
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={() => setShowSyllabusForm(true)}
                          aria-label="Create new syllabus"
                        >
                          Create Syllabus
                        </Button>
                      </Box>
                    )}

                  {activeEventSyllabusStatus === 'pending' && (
                    <Box
                      sx={{
                        p: 3,
                        bgcolor: 'background.paperDark',
                        borderRadius: 1,
                        textAlign: 'center',
                      }}
                    >
                      <Typography color="text.secondary" variant="body1">
                        Loading syllabus...
                      </Typography>
                    </Box>
                  )}

                  {activeEventSyllabus && (
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                    >
                      <SyllabusFieldEditable
                        eventID={activeEvent}
                        syllabus={activeEventSyllabus}
                        fieldName="text"
                        fieldLabel="Syllabus Overview (Main title screen text)"
                        fieldType="textarea"
                        complianceStatus={activeEventCompliance?.description}
                        required
                        viewMode="inline"
                      />

                      <SyllabusFieldEditable
                        eventID={activeEvent}
                        syllabus={activeEventSyllabus}
                        fieldName="practiceGap"
                        fieldLabel="Practice Gap"
                        fieldType="textarea"
                        complianceStatus={activeEventCompliance?.practiceGap}
                        required
                        viewMode="inline"
                      />

                      <SyllabusFieldEditable
                        eventID={activeEvent}
                        syllabus={activeEventSyllabus}
                        fieldName="practiceGapEvidence"
                        fieldLabel="Practice Gap Evidence"
                        fieldType="textarea"
                        complianceStatus={
                          activeEventCompliance?.practiceGapEvidence
                        }
                        required
                        viewMode="inline"
                      />

                      <SyllabusFieldEditable
                        eventID={activeEvent}
                        syllabus={activeEventSyllabus}
                        fieldName="learnerEngagement"
                        fieldLabel="Learner Engagement"
                        fieldType="textarea"
                        complianceStatus={
                          activeEventCompliance?.learnerEngagement
                        }
                        required
                        viewMode="inline"
                      />

                      <SyllabusFieldEditable
                        eventID={activeEvent}
                        syllabus={activeEventSyllabus}
                        fieldName="learningOutcomes"
                        fieldLabel="Learning Outcomes"
                        fieldType="textarea"
                        complianceStatus={
                          activeEventCompliance?.learningOutcomes
                        }
                        required
                        viewMode="inline"
                      />

                      <Box sx={{ mb: 4 }}>
                        <ReferencesManagement
                          eventID={activeEvent}
                          references={activeEventReferences}
                          referencesStatus={activeEventReferencesStatus}
                          complianceStatus={activeEventCompliance?.references}
                        />
                      </Box>

                      <ObjectivesManagement
                        eventID={activeEvent}
                        objectives={activeEventObjectives}
                        objectivesStatus={activeEventObjectivesStatus}
                        complianceStatus={activeEventCompliance?.objectives}
                      />
                    </Box>
                  )}
                </Box>
              )}

              {/* Tab 3: Assessment */}
              {activeTab === 3 && (
                <Box role="tabpanel" id="tabpanel-3" aria-labelledby="tab-3">
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    sx={{
                      mb: 3,
                    }}
                  >
                    Create and manage quiz questions to assess student
                    knowledge.
                  </Typography>

                  {activeEventQuiz && (
                    <Box sx={{ mb: 3 }}>
                      <Button
                        color="primary"
                        variant="outlined"
                        size="small"
                        onClick={() => setShowQuizDataModal(true)}
                        aria-label="View migrated quiz data for this event"
                        sx={{
                          mb: 2,
                        }}
                      >
                        View Migrated Quiz Data (Temporary)
                      </Button>
                    </Box>
                  )}

                  {activeEventCompliance?.quiz && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        p: 2,
                        bgcolor: 'background.paperDark',
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 'medium',
                        }}
                      >
                        Quiz Compliance Status
                      </Typography>
                      <Chip
                        label={
                          activeEventCompliance.quiz === 'approved'
                            ? 'Approved'
                            : activeEventCompliance.quiz === 'rejected'
                              ? 'Rejected'
                              : activeEventCompliance.quiz === 'updated'
                                ? 'Updated - Pending Review'
                                : activeEventCompliance.quiz === 'unapproved'
                                  ? 'Unapproved'
                                  : 'Pending Review'
                        }
                        color={
                          activeEventCompliance.quiz === 'approved'
                            ? 'success'
                            : activeEventCompliance.quiz === 'rejected' ||
                                activeEventCompliance.quiz === 'unapproved'
                              ? 'error'
                              : 'primary'
                        }
                      />
                    </Box>
                  )}

                  {!activeEventQuiz && activeEventQuizStatus !== 'pending' && (
                    <Box
                      sx={{
                        p: 3,
                        bgcolor: 'background.paperDark',
                        borderRadius: 1,
                        textAlign: 'center',
                        mb: 3,
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        No Quiz Created
                      </Typography>
                      <Typography
                        color="text.secondary"
                        variant="body2"
                        sx={{
                          mb: 2,
                        }}
                      >
                        Create a quiz to assess student knowledge at the end of
                        this event.
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => {
                          dispatch(
                            createQuiz({
                              eventID: activeEvent,
                              passingScore: 0,
                              displayedQuestions: 0,
                              randomizeQuestions: true,
                            }),
                          )
                        }}
                        aria-label="Create new quiz"
                      >
                        Create Quiz
                      </Button>
                    </Box>
                  )}

                  {activeEventQuizStatus === 'pending' && (
                    <Box
                      sx={{
                        p: 3,
                        bgcolor: 'background.paperDark',
                        borderRadius: 1,
                        textAlign: 'center',
                      }}
                      role="status"
                      aria-live="polite"
                      aria-busy="true"
                    >
                      <Typography color="text.secondary" variant="body1">
                        Loading quiz...
                      </Typography>
                    </Box>
                  )}

                  {activeEventQuiz && (
                    <QuizBuilder
                      quiz={activeEventQuiz}
                      fetchQuiz={fetchQuiz}
                      fetchQuizBy={activeEvent}
                      questionData={activeEventQuizQuestions || []}
                      fetchQuestionData={fetchQuizQuestions}
                      apiPath="/api/pipeline/planning/events"
                      aria-label="Quiz builder form for managing assessment questions"
                      submitButton={
                        <Box
                          sx={{
                            mt: 5,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1,
                          }}
                        >
                          <Button
                            color="secondary"
                            type="submit"
                            variant="contained"
                            aria-label="Save quiz changes"
                          >
                            Save Changes
                          </Button>
                        </Box>
                      }
                    />
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <EventCreationForm
        open={showEventForm}
        onClose={() => {
          setShowEventForm(false)
          setEditingEvent(undefined)
          // Refresh events after creation/update
          dispatch(fetchPlanningEvents())
        }}
        event={editingEvent}
      />
      <SyllabusCreationForm
        open={showSyllabusForm}
        onClose={() => {
          setShowSyllabusForm(false)
          // Refresh events progress after syllabus creation
          dispatch(fetchPlanningEvents())
        }}
        eventID={activeEvent || ''}
        eventName={activeEventData?.name}
      />
      <CAPCECreationForm
        open={showCAPCEForm}
        onClose={() => {
          setShowCAPCEForm(false)
          // Refresh events progress after CAPCE creation
          dispatch(fetchPlanningEvents())
        }}
        eventID={activeEvent || ''}
        eventName={activeEventData?.name}
      />
      <ANCCCreationForm
        open={showANCCForm}
        onClose={() => {
          setShowANCCForm(false)
          // Refresh events progress after ANCC creation
          dispatch(fetchPlanningEvents())
        }}
        eventID={activeEvent || ''}
        eventName={activeEventData?.name}
      />
      <QuizDataModal
        open={showQuizDataModal}
        onClose={() => setShowQuizDataModal(false)}
        eventName={activeEventData?.name || ''}
      />
    </Box>
  )
}
